import { EventEmitter } from 'events';
import { TwitchatDataTypes } from '@/types/TwitchatDataTypes';
import Utils from '@/utils/Utils';
import StoreProxy from '@/store/StoreProxy';

/**
 * Server-mode Chat Service
 * 
 * WebSocket-based chat service that communicates with the backend TMI.js service.
 * Implements the same interface as the browser TwitchMessengerClient for seamless integration.
 */
export default class ServerChatService extends EventEmitter {
    private static _instance: ServerChatService;
    private _websocket: WebSocket | null = null;
    private _isConnected = false;
    private _isConnecting = false;
    private _messageQueue: any[] = [];
    private _reconnectAttempts = 0;
    private _maxReconnectAttempts = 5;
    private _reconnectDelay = 3000;
    private _heartbeatInterval: number | null = null;
    private _connectedChannels = new Set<string>();

    // Message handlers
    private _messageHandlers = new Map<string, (data: any) => void>();

    private constructor() {
        super();
        this._setupMessageHandlers();
    }

    /**
     * Singleton instance getter
     */
    static get instance(): ServerChatService {
        if (!ServerChatService._instance) {
            ServerChatService._instance = new ServerChatService();
        }
        return ServerChatService._instance;
    }

    /**
     * Connect to backend WebSocket server
     */
    async connect(): Promise<void> {
        if (this._isConnected || this._isConnecting) {
            console.warn('ServerChatService: Already connected or connecting');
            return;
        }

        this._isConnecting = true;
        console.info('ServerChatService: Starting connection process...');

        try {
            const wsUrl = this._getWebSocketUrl();
            console.info(`ServerChatService: Attempting to connect to ${wsUrl}`);

            this._websocket = new WebSocket(wsUrl);
            this._setupWebSocketHandlers();

            // Wait for connection to be established
            await new Promise<void>((resolve, reject) => {
                const timeout = setTimeout(() => {
                    console.error('ServerChatService: WebSocket connection timeout after 10 seconds');
                    reject(new Error('WebSocket connection timeout'));
                }, 10000);

                this._websocket!.onopen = () => {
                    clearTimeout(timeout);
                    this._isConnected = true;
                    this._isConnecting = false;
                    this._reconnectAttempts = 0;
                    this._startHeartbeat();
                    this._processMessageQueue();
                    console.info('ServerChatService: Successfully connected to backend WebSocket');
                    resolve();
                };

                this._websocket!.onerror = (error) => {
                    clearTimeout(timeout);
                    this._isConnecting = false;
                    console.error('ServerChatService: WebSocket connection error:', error);
                    reject(error);
                };
            });

        } catch (error) {
            this._isConnecting = false;
            console.error('ServerChatService: Failed to connect to backend:', error);
            await this._handleReconnection();
            throw error;
        }
    }

    /**
     * Disconnect from backend
     */
    async disconnect(): Promise<void> {
        if (!this._websocket || !this._isConnected) {
            return;
        }

        this._stopHeartbeat();
        this._websocket.close();
        this._isConnected = false;
        this._connectedChannels.clear();
        console.info('ServerChatService: Disconnected from backend');
    }

    /**
     * Connect to Twitch chat with credentials
     */
    async connectToChat(credentials: { 
        username: string; 
        accessToken: string; 
        pinnedChannels?: { id: string; platform: string; login: string }[];
    }): Promise<void> {
        if (!this._isConnected) {
            console.error('ServerChatService: Cannot connect to chat - not connected to backend server');
            throw new Error('Not connected to backend server');
        }

        console.info(`ServerChatService: Initiating Twitch chat connection for user: ${credentials.username}`);
        
        const message = {
            type: 'chat_connect',
            id: Utils.getUUID(),
            version: '1.0.0',
            timestamp: Date.now(),
            data: {
                credentials,
            },
        };

        console.debug('ServerChatService: Sending chat_connect message:', {
            messageId: message.id,
            username: credentials.username,
            pinnedChannelsCount: credentials.pinnedChannels?.length || 0
        });

        await this._sendMessage(message);
        console.info(`ServerChatService: Chat connection request sent for ${credentials.username}`);
        if (credentials.pinnedChannels && credentials.pinnedChannels.length > 0) {
            console.info(`ServerChatService: Will connect to ${credentials.pinnedChannels.length} pinned channels:`, 
                credentials.pinnedChannels.map(c => c.login));
        }
    }

    /**
     * Disconnect from Twitch chat
     */
    async disconnectFromChat(): Promise<void> {
        if (!this._isConnected) {
            return;
        }

        const message = {
            type: 'chat_disconnect',
            id: Utils.getUUID(),
            version: '1.0.0',
            timestamp: Date.now(),
        };

        await this._sendMessage(message);
        console.info('ServerChatService: Disconnecting from Twitch chat');
    }

    /**
     * Join a Twitch channel
     */
    async joinChannel(channelLogin: string, channelId?: string): Promise<void> {
        if (!this._isConnected) {
            throw new Error('Not connected to backend server');
        }

        const message = {
            type: 'chat_join_channel',
            id: Utils.getUUID(),
            version: '1.0.0',
            timestamp: Date.now(),
            data: {
                channelLogin: channelLogin.toLowerCase(),
                channelId,
            },
        };

        await this._sendMessage(message);
        this._connectedChannels.add(channelLogin.toLowerCase());
        console.info(`ServerChatService: Joining channel ${channelLogin}`);
    }

    /**
     * Leave a Twitch channel
     */
    async leaveChannel(channelLogin: string): Promise<void> {
        if (!this._isConnected) {
            return;
        }

        const message = {
            type: 'chat_leave_channel',
            id: Utils.getUUID(),
            version: '1.0.0',
            timestamp: Date.now(),
            data: {
                channelLogin: channelLogin.toLowerCase(),
            },
        };

        await this._sendMessage(message);
        this._connectedChannels.delete(channelLogin.toLowerCase());
        console.info(`ServerChatService: Leaving channel ${channelLogin}`);
    }

    /**
     * Send a message to a channel
     */
    async sendMessage(channelLogin: string, message: string, replyTo?: string): Promise<void> {
        if (!this._isConnected) {
            throw new Error('Not connected to backend server');
        }

        const wsMessage = {
            type: 'chat_send_message',
            id: Utils.getUUID(),
            version: '1.0.0',
            timestamp: Date.now(),
            data: {
                channelLogin: channelLogin.toLowerCase(),
                message,
                replyTo,
            },
        };

        await this._sendMessage(wsMessage);
        console.info(`ServerChatService: Sending message to ${channelLogin}: ${message}`);
    }

    /**
     * Request chat history
     */
    async requestChatHistory(options?: { channelId?: string; limit?: number; before?: number }): Promise<void> {
        if (!this._isConnected) {
            throw new Error('Not connected to backend server');
        }

        const message = {
            type: 'chat_history_request',
            id: Utils.getUUID(),
            version: '1.0.0',
            timestamp: Date.now(),
            data: options || {},
        };

        await this._sendMessage(message);
        console.info('ServerChatService: Requesting chat history');
    }

    /**
     * Get connection status
     */
    getConnectionStatus(): { isConnected: boolean; connectedChannels: string[] } {
        return {
            isConnected: this._isConnected,
            connectedChannels: Array.from(this._connectedChannels),
        };
    }

    /**
     * Setup WebSocket event handlers
     */
    private _setupWebSocketHandlers(): void {
        if (!this._websocket) return;

        console.info('ServerChatService: Setting up WebSocket event handlers');

        this._websocket.onmessage = (event) => {
            try {
                const message = JSON.parse(event.data);
                console.debug('ServerChatService: Received WebSocket message:', message.type);
                this._handleWebSocketMessage(message);
            } catch (error) {
                console.error('ServerChatService: Failed to parse WebSocket message:', error, 'Raw data:', event.data);
            }
        };

        this._websocket.onclose = (event) => {
            this._isConnected = false;
            this._stopHeartbeat();
            console.info(`ServerChatService: WebSocket connection closed (code: ${event.code}, reason: "${event.reason}")`);
            
            // Attempt reconnection unless it was a clean close
            if (event.code !== 1000) {
                console.info('ServerChatService: WebSocket closed unexpectedly, attempting reconnection...');
                this._handleReconnection();
            } else {
                console.info('ServerChatService: WebSocket closed cleanly, no reconnection needed');
            }
        };

        this._websocket.onerror = (error) => {
            console.error('ServerChatService: WebSocket error occurred:', error);
        };
    }

    /**
     * Handle incoming WebSocket messages
     */
    private _handleWebSocketMessage(message: any): void {
        console.debug(`ServerChatService: Received message type: ${message.type}`);

        const handler = this._messageHandlers.get(message.type);
        if (handler) {
            // For error messages, pass the error data instead of data
            if (message.type === 'error') {
                handler(message.error);
            } else {
                handler(message.data);
            }
        } else {
            console.warn(`ServerChatService: No handler for message type: ${message.type}`);
        }
    }

    /**
     * Setup message type handlers
     */
    private _setupMessageHandlers(): void {
        // Connection acknowledgment
        this._messageHandlers.set('connection_ack', (data: any) => {
            console.info('ServerChatService: Connection acknowledged by server');
            this.emit('connected');
        });

        // Heartbeat acknowledgment
        this._messageHandlers.set('heartbeat_ack', (data: any) => {
            // Heartbeat acknowledged, connection is healthy
        });

        // Chat connect acknowledgment
        this._messageHandlers.set('chat_connect', (data: any) => {
            console.info('ServerChatService: Chat connection acknowledged');
            this.emit('chatConnected', data);
        });

        // Heartbeat (from server)
        this._messageHandlers.set('heartbeat', (data: any) => {
            // Respond to server heartbeat
            this._sendMessage({
                type: 'heartbeat_ack',
                id: Utils.getUUID(),
                version: '1.0.0',
                timestamp: Date.now(),
            });
        });

        // Chat message received
        this._messageHandlers.set('chat_message_received', (data: any) => {
            this._handleChatMessage(data);
        });

        // Chat state synchronization
        this._messageHandlers.set('chat_state_sync', (data: any) => {
            this._handleChatStateSync(data);
        });

        // Chat history response
        this._messageHandlers.set('chat_history_response', (data: any) => {
            this._handleChatHistory(data);
        });

        // Whisper message
        this._messageHandlers.set('chat_whisper', (data: any) => {
            this._handleWhisperMessage(data);
        });

        // User events
        this._messageHandlers.set('chat_user_join', (data: any) => {
            this.emit('userJoin', data);
        });

        this._messageHandlers.set('chat_user_part', (data: any) => {
            this.emit('userPart', data);
        });

        // Moderation events
        this._messageHandlers.set('chat_user_ban', (data: any) => {
            this.emit('userBan', data);
        });

        this._messageHandlers.set('chat_user_timeout', (data: any) => {
            this.emit('userTimeout', data);
        });

        this._messageHandlers.set('chat_message_deleted', (data: any) => {
            this.emit('messageDeleted', data);
        });

        this._messageHandlers.set('chat_clear_chat', (data: any) => {
            this.emit('clearChat', data);
        });

        // Subscription events
        this._messageHandlers.set('chat_subscription', (data: any) => {
            this.emit('subscription', data);
        });

        this._messageHandlers.set('chat_cheer', (data: any) => {
            this.emit('cheer', data);
        });

        this._messageHandlers.set('chat_raid', (data: any) => {
            this.emit('raid', data);
        });

        // Additional events that might be needed
        this._messageHandlers.set('chat_disconnect', (data: any) => {
            console.info('ServerChatService: Chat disconnected');
            this.emit('chatDisconnected', data);
        });

        this._messageHandlers.set('chat_join_channel', (data: any) => {
            console.info(`ServerChatService: Channel joined: ${data.channelLogin}`);
            this.emit('channelJoined', data);
        });

        this._messageHandlers.set('chat_leave_channel', (data: any) => {
            console.info(`ServerChatService: Channel left: ${data.channelLogin}`);
            this.emit('channelLeft', data);
        });

        // Error handling
        this._messageHandlers.set('error', (data: any) => {
            // Error data is in message.error, not message.data
            console.error('ServerChatService: Server error:', data);
            this.emit('error', data);
        });
    }

    /**
     * Handle incoming chat messages
     */
    private _handleChatMessage(data: any): void {
        // Use StoreProxy.users.getUserFrom() like the legacy system
        // This ensures proper user object creation with channelInfo
        const user = StoreProxy.users.getUserFrom(
            'twitch', 
            data.channel_id, 
            data.user.id, 
            data.user.login, 
            data.user.displayName, 
            undefined, 
            false, 
            true, 
            data.user.isSubscriber || false
        );

        // Update user properties from backend data
        if (data.user.color) user.color = data.user.color;
        
        // Update channel-specific info
        if (user.channelInfo[data.channel_id]) {
            if (data.user.isModerator) user.channelInfo[data.channel_id].is_moderator = true;
            if (data.user.isVip) user.channelInfo[data.channel_id].is_vip = true;
            if (data.user.isSubscriber) user.channelInfo[data.channel_id].is_subscriber = true;
            if (data.user.isBroadcaster) user.channelInfo[data.channel_id].is_broadcaster = true;
            if (data.user.badges) user.channelInfo[data.channel_id].badges = data.user.badges;
            user.channelInfo[data.channel_id].lastActivityDate = Date.now();
        }

        // Convert backend message format to frontend format
        const chatMessage: TwitchatDataTypes.MessageChatData = {
            id: data.id,
            date: data.timestamp,
            type: "message",
            platform: 'twitch',
            channel_id: data.channel_id,
            user,
            message: data.message,
            message_chunks: this._parseMessageToChunks(data.message),
            message_html: data.message,
            message_size: data.message.length,
            answers: [],
            is_short: data.message.length < 50,
        };

        this.emit('message', chatMessage);
    }

    /**
     * Handle chat state synchronization
     */
    private _handleChatStateSync(data: any): void {
        console.info('ServerChatService: Received chat state sync');
        
        // Update connected channels
        this._connectedChannels.clear();
        data.connectedChannels?.forEach((channel: string) => {
            this._connectedChannels.add(channel);
        });

        // Process message history and emit individual message events
        if (data.messageHistory && Array.isArray(data.messageHistory)) {
            console.info(`ServerChatService: Restoring ${data.messageHistory.length} messages from history`);
            
            // Convert and emit each message individually so the chat interface displays them
            data.messageHistory.forEach((msg: any) => {
                try {
                    const chatMessage = this._convertBackendMessage(msg);
                    this.emit('message', chatMessage);
                } catch (error) {
                    console.error('ServerChatService: Failed to convert message from history:', error, msg);
                }
            });
        }

        // Emit state sync event for other components that need it
        this.emit('stateSync', {
            isConnected: data.isConnected,
            connectedChannels: data.connectedChannels,
            messageHistory: data.messageHistory,
        });
    }

    /**
     * Handle chat history response
     */
    private _handleChatHistory(data: any): void {
        console.info(`ServerChatService: Received ${data.messages.length} messages from history`);
        
        // Convert messages to frontend format and emit
        const messages = data.messages.map((msg: any) => this._convertBackendMessage(msg));
        
        this.emit('chatHistory', {
            messages,
            hasMore: data.hasMore,
            total: data.total,
        });
    }

    /**
     * Handle whisper messages
     */
    private _handleWhisperMessage(data: any): void {
        const whisperMessage: TwitchatDataTypes.MessageWhisperData = {
            id: Utils.getUUID(),
            date: data.timestamp,
            type: "whisper",
            platform: 'twitch',
            channel_id: "whispers",
            user: {
                id: data.user.id,
                login: data.user.login,
                displayNameOriginal: data.user.displayName,
                displayName: data.user.displayName,
                color: data.user.color,
                badges: data.user.badges || [],
                platform: 'twitch',
                channelInfo: {},
                is_partner: false,
                is_affiliate: false,
                is_tracked: false,
                is_bot: false,
                is_blocked: false,
                pronouns: null,
                pronounsLabel: false,
                pronounsTooltip: false,
            } as TwitchatDataTypes.TwitchatUser,
            to: {
                id: "self",
                login: "self",
                displayNameOriginal: "Self",
                displayName: "Self",
                platform: 'twitch',
                channelInfo: {},
                is_partner: false,
                is_affiliate: false,
                is_tracked: false,
                is_bot: false,
                is_blocked: false,
                pronouns: null,
                pronounsLabel: false,
                pronounsTooltip: false,
            } as TwitchatDataTypes.TwitchatUser,
            message: data.message,
            message_chunks: this._parseMessageToChunks(data.message),
            message_html: data.message,
            message_size: data.message.length,
        };

        this.emit('whisper', whisperMessage);
    }

    /**
     * Convert backend message to frontend format
     */
    private _convertBackendMessage(msg: any): TwitchatDataTypes.MessageChatData {
        // Use StoreProxy.users.getUserFrom() for consistent user object creation
        const user = StoreProxy.users.getUserFrom(
            'twitch', 
            msg.channel_id, 
            msg.user.id, 
            msg.user.login, 
            msg.user.displayName, 
            undefined, 
            false, 
            true, 
            msg.user.isSubscriber || false
        );

        // Update user properties from backend data
        if (msg.user.color) user.color = msg.user.color;
        
        // Update channel-specific info
        if (user.channelInfo[msg.channel_id]) {
            if (msg.user.isModerator) user.channelInfo[msg.channel_id].is_moderator = true;
            if (msg.user.isVip) user.channelInfo[msg.channel_id].is_vip = true;
            if (msg.user.isSubscriber) user.channelInfo[msg.channel_id].is_subscriber = true;
            if (msg.user.isBroadcaster) user.channelInfo[msg.channel_id].is_broadcaster = true;
            if (msg.user.badges) user.channelInfo[msg.channel_id].badges = msg.user.badges;
            user.channelInfo[msg.channel_id].lastActivityDate = Date.now();
        }

        return {
            id: msg.id,
            date: msg.timestamp,
            type: "message",
            platform: 'twitch',
            channel_id: msg.channel_id,
            user,
            message: msg.message,
            message_chunks: this._parseMessageToChunks(msg.message),
            message_html: msg.message,
            message_size: msg.message.length,
            answers: [],
            is_short: msg.message.length < 50,
        };
    }

    /**
     * Parse message to chunks (simplified version)
     */
    private _parseMessageToChunks(message: string): TwitchatDataTypes.ParseMessageChunk[] {
        // Basic text chunk for now - would need full parsing logic for emotes/links
        return [{
            type: 'text',
            value: message,
        }];
    }

    /**
     * Send message through WebSocket
     */
    private async _sendMessage(message: any): Promise<void> {
        if (!this._websocket || this._websocket.readyState !== WebSocket.OPEN) {
            console.warn('ServerChatService: WebSocket not ready, queuing message:', message.type);
            // Queue message for later if not connected
            this._messageQueue.push(message);
            return;
        }

        try {
            const messageStr = JSON.stringify(message);
            console.debug('ServerChatService: Sending WebSocket message:', message.type, 'Length:', messageStr.length);
            this._websocket.send(messageStr);
        } catch (error) {
            console.error('ServerChatService: Failed to send WebSocket message:', error, 'Message type:', message.type);
            throw error;
        }
    }

    /**
     * Process queued messages
     */
    private _processMessageQueue(): void {
        while (this._messageQueue.length > 0) {
            const message = this._messageQueue.shift();
            this._sendMessage(message);
        }
    }

    /**
     * Start heartbeat to keep connection alive
     */
    private _startHeartbeat(): void {
        if (this._heartbeatInterval) {
            clearInterval(this._heartbeatInterval);
        }

        this._heartbeatInterval = window.setInterval(() => {
            if (this._isConnected && this._websocket) {
                const heartbeat = {
                    type: 'heartbeat',
                    id: Utils.getUUID(),
                    version: '1.0.0',
                    timestamp: Date.now(),
                };
                this._sendMessage(heartbeat);
            }
        }, 30000); // 30 seconds
    }

    /**
     * Stop heartbeat
     */
    private _stopHeartbeat(): void {
        if (this._heartbeatInterval) {
            clearInterval(this._heartbeatInterval);
            this._heartbeatInterval = null;
        }
    }

    /**
     * Handle reconnection logic
     */
    private async _handleReconnection(): Promise<void> {
        if (this._reconnectAttempts >= this._maxReconnectAttempts) {
            console.error('ServerChatService: Max reconnection attempts reached');
            this.emit('reconnectionFailed');
            return;
        }

        this._reconnectAttempts++;
        const delay = this._reconnectDelay * this._reconnectAttempts;

        console.info(`ServerChatService: Reconnecting in ${delay}ms (attempt ${this._reconnectAttempts}/${this._maxReconnectAttempts})`);

        setTimeout(async () => {
            try {
                await this.connect();
            } catch (error) {
                console.error('ServerChatService: Reconnection failed:', error);
                await this._handleReconnection();
            }
        }, delay);
    }

    /**
     * Get WebSocket URL based on current configuration
     */
    private _getWebSocketUrl(): string {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const host = window.location.hostname;
        
        // Use the same port as the main server for WebSocket
        const port = window.location.port || (window.location.protocol === 'https:' ? '443' : '80');
        const path = '/websocket'; // Use the configured path from server mode config
        
        const wsUrl = `${protocol}//${host}:${port}${path}`;
        
        // Debug logging
        console.log('ServerChatService: WebSocket URL generation:', {
            protocol,
            host,
            port,
            path,
            wsUrl
        });
        
        return wsUrl;
    }
}